/**
 * Lazily loads OpenCV.js, self-hosted under public/opencv/opencv.js (not an
 * npm dependency — its package has no "browser" field, so bundling it
 * through webpack fights unresolved Node core-module references in the
 * Emscripten glue code; a plain <script> tag sidesteps that, matching this
 * app's existing MediaPipe self-hosting precedent). Cached at module scope
 * so front->back capture in one session doesn't reload it.
 *
 * This build uses Emscripten's modern MODULARIZE output, where the UMD
 * factory is itself an async function — so `window.cv` is a Promise that
 * resolves to the ready module, not a synchronously-assigned object with an
 * `onRuntimeInitialized` callback (that pattern is only for the classic,
 * older opencv.js builds).
 *
 * Custom-built (not @techstark/opencv-js's stock artifact) to strip modules
 * this app never calls — documentQuality.js/documentAligner.js only use
 * core+imgproc (resize, threshold, cvtColor, warpPerspective,
 * getPerspectiveTransform, findContours, moments, Laplacian, meanStdDev).
 * The stock build ships calib/dnn/features/flann/geometry/objdetect/photo/
 * ptcloud/stereo/video too (13.3MB); this one is 5.0MB. Validated
 * byte-identical output against the stock build across the full
 * warp+blur+heatmap-decode pipeline before swapping in.
 *
 * To rebuild (e.g. to pick up a newer OpenCV release):
 *   git clone https://github.com/emscripten-core/emsdk && cd emsdk
 *   ./emsdk install 4.0.20 && ./emsdk activate 4.0.20 && source emsdk_env.sh
 *   cd .. && git clone --depth 1 --branch 5.0.0 https://github.com/opencv/opencv
 *   emcmake python3 opencv/platforms/js/build_js.py build_js \
 *     --cmake_option="-DCMAKE_CXX_STANDARD=17" \
 *     --cmake_option="-DBUILD_opencv_dnn=OFF" --cmake_option="-DBUILD_opencv_calib=OFF" \
 *     --cmake_option="-DBUILD_opencv_features=OFF" --cmake_option="-DBUILD_opencv_objdetect=OFF" \
 *     --cmake_option="-DBUILD_opencv_photo=OFF" --cmake_option="-DBUILD_opencv_ptcloud=OFF" \
 *     --cmake_option="-DBUILD_opencv_stereo=OFF" --cmake_option="-DBUILD_opencv_video=OFF"
 *   (flann/geometry must stay ON — imgproc transitively depends on both via
 *   flann->geometry->imgproc in OpenCV 5.0.0, a new dependency vs. 4.x; disabling
 *   either silently drops imgproc's JS bindings with no build error, just missing
 *   functions at runtime.) Also needs a one-line source patch: OpenCV 5.0.0's
 *   modules/js/src/core_bindings.cpp:90 has an unguarded
 *   `using namespace cv::segmentation;` that only compiles when `photo` is
 *   enabled — wrap it in `#ifdef HAVE_OPENCV_PHOTO` (upstream's own code marks
 *   this line `// FIXIT`, i.e. a known bug, not something we introduced).
 */

let cvLoadPromise = null;

export function loadOpenCv() {
  if (!cvLoadPromise) {
    let script = null;
    cvLoadPromise = new Promise((resolve, reject) => {
      if (window.cv && window.cv.Mat) {
        resolve(window.cv);
        return;
      }
      script = document.createElement("script");
      script.src = "/opencv/opencv.js";
      script.async = true;
      script.onload = () => {
        if (!window.cv) {
          reject(new Error("opencv.js loaded but window.cv is undefined"));
          return;
        }
        Promise.resolve(window.cv).then(resolve, reject);
      };
      script.onerror = () => reject(new Error("Failed to load /opencv/opencv.js"));
      document.head.appendChild(script);
    }).catch((err) => {
      // Don't cache a failure: the next camera mount (retake, back side)
      // should try again rather than get this rejection for the rest of the
      // tab. Drop the dead <script> so a retry doesn't stack another one.
      cvLoadPromise = null;
      script?.remove();
      throw err;
    });
  }
  return cvLoadPromise;
}

/** Tracks every cv.Mat/MatVector allocated during one pass so they can all
 * be freed together — OpenCV.js Mats are Emscripten-heap allocated, not
 * garbage collected. */
export class MatTracker {
  constructor() {
    this.items = [];
  }

  add(item) {
    this.items.push(item);
    return item;
  }

  deleteAll() {
    for (const item of this.items) {
      try {
        item.delete();
      } catch (_e) {
        // already deleted / not deletable — ignore
      }
    }
    this.items = [];
  }
}
